import type { z } from "zod";

/** Zod issues as `path: message` lines — what a toast or CLI can print verbatim. */
function formatIssues(error: z.ZodError): string[] {
  return error.issues.map((issue) => {
    const path = issue.path.map(String).join(".");
    return path ? `${path}: ${issue.message}` : issue.message;
  });
}

/**
 * Parse untrusted JSON into an exported bundle (procedure, agent, …), or a flat list of readable
 * errors. An unknown `formatVersion` gets its own message rather than a wall of schema issues.
 */
export function parseVersionedBundle<S extends z.ZodType>(
  raw: unknown,
  options: { schema: S; formatVersion: number; label: string },
): { ok: true; bundle: z.output<S> } | { ok: false; errors: string[] } {
  if (typeof raw !== "object" || raw === null) {
    return { ok: false, errors: ["Expected a JSON object."] };
  }
  if ("formatVersion" in raw && raw.formatVersion !== options.formatVersion) {
    return {
      ok: false,
      errors: [
        `Unsupported ${options.label} file formatVersion ${String(raw.formatVersion)} (expected ${options.formatVersion}).`,
      ],
    };
  }
  const parsed = options.schema.safeParse(raw);
  return parsed.success
    ? { ok: true, bundle: parsed.data }
    : { ok: false, errors: formatIssues(parsed.error) };
}
